import { fetchServiceIdentifyWatch } from '@/lib/myWatches/identifyClient';
import { WarrantyStatus } from '@/lib/myWatches/types';
import type { Watch } from '@/lib/myWatches/types';
import type { WatchProductSummary } from '@/lib/service/ecs/contracts';
import { fetchWatchDetails } from '@/lib/service/watchDetailsClient';
import { isWatchReferenceInput } from '@/lib/service/watchReferencePatterns';

export type WatchInputKind = 'serial' | 'reference';

export type ResolveWatchReferenceResult =
  | {
      status: 'identified';
      inputKind: WatchInputKind;
      referenceNumber: string;
      watchProductSummary: WatchProductSummary;
      watchWarranty?: Watch;
      serialNumber?: string;
    }
  | {
      status: 'not-found';
      inputKind: WatchInputKind;
      serialNumber?: string;
    }
  | {
      status: 'error';
      message?: string;
    };

export async function resolveWatchReference(params: {
  input: string;
  locale: string;
  countryCode: string;
  /** Repair request flows: identify via serial only, never treat input as a reference. */
  serialOnly?: boolean;
}): Promise<ResolveWatchReferenceResult> {
  const trimmed = params.input.trim();
  if (!trimmed) {
    return { status: 'not-found', inputKind: 'reference' };
  }

  if (!params.serialOnly && isWatchReferenceInput(trimmed)) {
    return resolveByReference(trimmed, params);
  }

  return resolveBySerial(trimmed, params);
}

async function resolveByReference(
  referenceNumber: string,
  params: { locale: string; countryCode: string }
): Promise<ResolveWatchReferenceResult> {
  try {
    const watchProductSummary = await fetchWatchDetails({
      countryCode: params.countryCode,
      locale: params.locale,
      watchReference: referenceNumber,
    });

    return {
      status: 'identified',
      inputKind: 'reference',
      referenceNumber,
      watchProductSummary,
    };
  } catch {
    return { status: 'not-found', inputKind: 'reference' };
  }
}

async function resolveBySerial(
  serialNumber: string,
  params: { locale: string; countryCode: string }
): Promise<ResolveWatchReferenceResult> {
  const warrantyResult = await fetchServiceIdentifyWatch({
    sn: serialNumber,
    locale: params.locale,
  });

  if (!warrantyResult) {
    return { status: 'error' };
  }

  if (!warrantyResult.data?.sku || warrantyResult.code === WarrantyStatus.NotFound) {
    return { status: 'not-found', inputKind: 'serial', serialNumber };
  }

  const referenceNumber = warrantyResult.data.sku;

  try {
    const watchProductSummary = await fetchWatchDetails({
      countryCode: params.countryCode,
      locale: params.locale,
      watchReference: referenceNumber,
    });

    return {
      status: 'identified',
      inputKind: 'serial',
      referenceNumber,
      watchProductSummary,
      watchWarranty: warrantyResult.data,
      serialNumber,
    };
  } catch {
    return { status: 'not-found', inputKind: 'serial', serialNumber };
  }
}
